<template>
    <main class="roles-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('menu.companies.roles') }}</div>
        </portal>

        <header class="roles-hero">
            <div class="roles-hero-row">
                <div>
                    <h1 class="roles-hero-title">{{ $t('menu.companies.roles') }}</h1>
                    <p class="roles-hero-sub">{{ $t('roles.page_subtitle') }}</p>
                </div>
                <BmvButton variant="primary" icon="fal fa-plus" @click="openCreate">
                    {{ $t('roles.new') }}
                </BmvButton>
            </div>
        </header>

        <section class="roles-card">
            <div v-if="isLoading" class="roles-state">
                <BmvLoader :active="true" />
            </div>

            <div v-else-if="loadError" class="roles-state roles-state--error">
                <i class="fal fa-exclamation-triangle"></i>
                <p>{{ $t('roles.load_error') }}</p>
                <BmvButton variant="primary" size="sm" @click="loadRoles">
                    {{ $t('roles.retry') }}
                </BmvButton>
            </div>

            <div v-else-if="!visibleRoles.length" class="roles-state roles-empty">
                <i class="fal fa-user-tag"></i>
                <p>{{ $t('roles.empty') }}</p>
            </div>

            <ul v-else class="roles-list">
                <li v-for="role in visibleRoles" :key="role.id" class="roles-row">
                    <div class="roles-row-avatar">
                        <i class="fal fa-user-tag"></i>
                    </div>

                    <div class="roles-row-body">
                        <div class="roles-row-heading">
                            <h3 class="roles-row-title">{{ displayName(role) }}</h3>
                            <span v-if="isTemplate(role)" class="roles-tag roles-tag--system">
                                <i class="fal fa-shapes"></i>
                                {{ $t('roles.system') }}
                            </span>
                        </div>
                        <p v-if="displayDescription(role)" class="roles-row-sub">
                            {{ displayDescription(role) }}
                        </p>
                        <div class="roles-row-meta">
                            <span class="roles-meta-item">
                                <i class="fal fa-shield-check"></i>
                                {{ permissionsCountLabel(role) }}
                            </span>
                            <span v-if="usersCount(role) !== null" class="roles-meta-item">
                                <i class="fal fa-users"></i>
                                {{ usersCountLabel(role) }}
                            </span>
                            <span class="roles-meta-item roles-meta-item--slug">
                                <i class="fal fa-hashtag"></i>
                                {{ role.name }}
                            </span>
                        </div>
                    </div>

                    <div class="roles-row-actions">
                        <BmvButton
                            variant="secondary"
                            size="sm"
                            icon="fal fa-shield-check"
                            @click="openPermissions(role)"
                        >
                            {{ $t('roles.manage_permissions') }}
                        </BmvButton>
                        <BmvButton
                            variant="ghost"
                            size="sm"
                            icon="fal fa-pen"
                            :title="$t('common.edit')"
                            @click="openEdit(role)"
                        />
                        <BmvButton
                            variant="ghost"
                            size="sm"
                            icon="fal fa-clone"
                            :title="$t('roles.duplicate')"
                            @click="openDuplicate(role)"
                        />
                        <BmvButton
                            variant="danger"
                            size="sm"
                            icon="fal fa-trash"
                            :title="$t('common.delete')"
                            @click="openDelete(role)"
                        />
                    </div>
                </li>
            </ul>
        </section>

        <!-- Création / renommage / duplication -->
        <BmvModal
            v-model="modalOpen"
            size="md"
            :closable="!isSaving"
            :close-on-overlay="!isSaving"
            :close-on-esc="!isSaving"
            @close="cancelModal"
        >
            <template #header>
                <h2 class="roles-modal-title">{{ modalTitle }}</h2>
            </template>

            <div v-if="editing" class="roles-modal-body">
                <div class="roles-field">
                    <label class="roles-field-label" for="role-label">
                        {{ $t('roles.label') }}
                    </label>
                    <input
                        id="role-label"
                        v-model.trim="editing.label"
                        type="text"
                        class="roles-input"
                        :placeholder="$t('roles.label_placeholder')"
                        maxlength="60"
                        @keyup.enter="save"
                    />
                    <p class="roles-field-hint">{{ $t('roles.label_hint') }}</p>
                </div>

                <div v-if="editing.mode !== 'duplicate'" class="roles-field">
                    <label class="roles-field-label" for="role-description">
                        {{ $t('roles.description') }}
                    </label>
                    <textarea
                        id="role-description"
                        v-model.trim="editing.description"
                        class="roles-input roles-textarea"
                        rows="3"
                        :placeholder="$t('roles.description_placeholder')"
                        maxlength="255"
                    ></textarea>
                </div>

                <p v-if="editing.mode === 'create'" class="roles-modal-note">
                    <i class="fal fa-info-circle"></i>
                    {{ $t('roles.create_note') }}
                </p>
                <p v-else-if="editing.mode === 'duplicate'" class="roles-modal-note">
                    <i class="fal fa-info-circle"></i>
                    {{ $t('roles.duplicate_note', { name: editing.sourceName }) }}
                </p>
            </div>

            <template #footer>
                <BmvButton variant="ghost" size="md" :disabled="isSaving" @click="cancelModal">
                    {{ $t('common.cancel') }}
                </BmvButton>
                <BmvButton
                    variant="primary"
                    size="md"
                    icon="fal fa-save"
                    :loading="isSaving"
                    :disabled="!canSave"
                    @click="save"
                >
                    {{ $t('common.save') }}
                </BmvButton>
            </template>
        </BmvModal>

        <!-- Suppression + réaffectation des utilisateurs -->
        <BmvModal
            v-model="deleteOpen"
            size="md"
            :closable="!isDeleting"
            :close-on-overlay="!isDeleting"
            :close-on-esc="!isDeleting"
            @close="cancelDelete"
        >
            <template #header>
                <h2 class="roles-modal-title">
                    {{ $t('roles.delete_title', { name: deletingName }) }}
                </h2>
            </template>

            <div v-if="deleting" class="roles-modal-body">
                <p class="roles-modal-text">{{ $t('roles.delete_warning') }}</p>

                <div v-if="deletingUsersCount" class="roles-field">
                    <label class="roles-field-label" for="role-reassign">
                        {{ $t('roles.reassign_label', { count: deletingUsersCount }) }}
                    </label>
                    <select id="role-reassign" v-model="reassignTo" class="roles-input">
                        <option :value="null">{{ $t('roles.reassign_none') }}</option>
                        <option
                            v-for="role in reassignOptions"
                            :key="role.id"
                            :value="role.id"
                        >
                            {{ displayName(role) }}
                        </option>
                    </select>
                    <p class="roles-field-hint">
                        {{ reassignTo ? $t('roles.reassign_hint') : $t('roles.reassign_none_hint') }}
                    </p>
                </div>
            </div>

            <template #footer>
                <BmvButton variant="ghost" size="md" :disabled="isDeleting" @click="cancelDelete">
                    {{ $t('common.cancel') }}
                </BmvButton>
                <BmvButton
                    variant="danger"
                    size="md"
                    icon="fal fa-trash"
                    :loading="isDeleting"
                    @click="confirmDelete"
                >
                    {{ $t('common.delete') }}
                </BmvButton>
            </template>
        </BmvModal>
    </main>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'
import BmvModal from '@/components/UI/v2/BmvModal.vue'
import {
    getRoleSlug,
    isTemplateRole,
    roleDisplayDescription,
    roleDisplayName,
    rolePermissions,
    roleUsersCount
} from '@/helpers/roles'

export default {
    name: 'CompanyRoles',
    components: { BmvButton, BmvLoader, BmvModal },
    data() {
        return {
            isLoading: true,
            loadError: false,
            modalOpen: false,
            isSaving: false,
            editing: null,
            deleteOpen: false,
            isDeleting: false,
            deleting: null,
            reassignTo: null
        }
    },
    computed: {
        ...mapState('role', {
            allRoles: (state) => state.roles
        }),
        ...mapState('auth', {
            currentRole: (state) => state.role
        }),
        isSuperAdmin() {
            return getRoleSlug(this.currentRole) === 'super-admin'
        },
        visibleRoles() {
            if (this.isSuperAdmin) return this.allRoles
            return this.allRoles.filter((role) => getRoleSlug(role) !== 'super-admin')
        },
        modalTitle() {
            if (!this.editing) return ''
            if (this.editing.mode === 'edit') return this.$t('roles.form_title_edit')
            if (this.editing.mode === 'duplicate') return this.$t('roles.form_title_duplicate')
            return this.$t('roles.form_title_create')
        },
        canSave() {
            return !!(this.editing && (this.editing.label || '').trim().length > 1)
        },
        deletingName() {
            return this.deleting ? this.displayName(this.deleting) : ''
        },
        deletingUsersCount() {
            return this.deleting ? roleUsersCount(this.deleting) || 0 : 0
        },
        reassignOptions() {
            if (!this.deleting) return []
            return this.visibleRoles.filter(
                (role) => String(role.id) !== String(this.deleting.id)
            )
        }
    },
    created() {
        this.loadRoles()
    },
    methods: {
        ...mapActions('role', ['getRoles', 'createRole', 'updateRole', 'duplicateRole', 'deleteRole']),
        displayName(role) {
            return roleDisplayName(this.$i18n, role)
        },
        displayDescription(role) {
            return roleDisplayDescription(this.$i18n, role)
        },
        isTemplate(role) {
            return isTemplateRole(role)
        },
        usersCount(role) {
            return roleUsersCount(role)
        },
        permissionsCountLabel(role) {
            const count = rolePermissions(role).length
            const key = count === 1 ? 'roles.permissions_count_one' : 'roles.permissions_count_other'
            return this.$t(key, { count })
        },
        usersCountLabel(role) {
            const count = roleUsersCount(role)
            const key = count === 1 ? 'roles.users_count_one' : 'roles.users_count_other'
            return this.$t(key, { count })
        },
        async loadRoles() {
            this.isLoading = true
            this.loadError = false
            try {
                await this.getRoles()
            } catch (error) {
                this.loadError = true
                this.$store.dispatch('dispatchError', error)
            } finally {
                this.isLoading = false
            }
        },
        openCreate() {
            this.editing = { mode: 'create', id: null, label: '', description: '' }
            this.modalOpen = true
        },
        openEdit(role) {
            this.editing = {
                mode: 'edit',
                id: role.id,
                label: role.label || this.displayName(role),
                description: role.description || ''
            }
            this.modalOpen = true
        },
        openDuplicate(role) {
            this.editing = {
                mode: 'duplicate',
                id: role.id,
                sourceName: this.displayName(role),
                label: this.$t('roles.duplicate_label', { name: this.displayName(role) }),
                description: ''
            }
            this.modalOpen = true
        },
        openPermissions(role) {
            this.$router.push({
                name: 'CompanyRolePermissions',
                params: { roleId: String(role.id) }
            })
        },
        cancelModal() {
            if (this.isSaving) return
            this.modalOpen = false
            this.editing = null
        },
        async save() {
            if (!this.canSave || this.isSaving) return
            this.isSaving = true
            const label = this.editing.label.trim()
            const description = (this.editing.description || '').trim()
            try {
                let successKey = 'roles.created'
                if (this.editing.mode === 'edit') {
                    // `permissions` volontairement omis : l'API conserve les droits du rôle.
                    await this.updateRole({ id: this.editing.id, label, description })
                    successKey = 'roles.updated'
                } else if (this.editing.mode === 'duplicate') {
                    await this.duplicateRole({ id: this.editing.id, label })
                    successKey = 'roles.duplicated'
                } else {
                    await this.createRole({ label, description })
                }
                this.$store.dispatch('toast', {
                    type: 'success',
                    title: this.$t(successKey),
                    message: ''
                })
                this.modalOpen = false
                this.editing = null
            } catch (error) {
                this.$store.dispatch('dispatchError', error)
            } finally {
                this.isSaving = false
            }
        },
        openDelete(role) {
            this.deleting = role
            this.reassignTo = null
            this.deleteOpen = true
        },
        cancelDelete() {
            if (this.isDeleting) return
            this.deleteOpen = false
            this.deleting = null
            this.reassignTo = null
        },
        async confirmDelete() {
            if (!this.deleting || this.isDeleting) return
            this.isDeleting = true
            try {
                await this.deleteRole({ id: this.deleting.id, reassignTo: this.reassignTo })
                this.$store.dispatch('toast', {
                    type: 'success',
                    title: this.$t('roles.deleted'),
                    message: ''
                })
                this.deleteOpen = false
                this.deleting = null
                this.reassignTo = null
            } catch (error) {
                this.$store.dispatch('dispatchError', error)
            } finally {
                this.isDeleting = false
            }
        }
    }
}
</script>

<style scoped>
.roles-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.roles-hero {
    margin-bottom: var(--v2-space-5);
}

.roles-hero-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--v2-space-4);
    flex-wrap: wrap;
}

.roles-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.roles-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 820px;
}

.roles-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    min-height: 220px;
}

.roles-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-6) var(--v2-space-5);
    text-align: center;
    color: var(--v2-text-3);
}

.roles-state--error i {
    font-size: 1.75rem;
    color: var(--v2-warning);
}

.roles-empty i {
    font-size: 2rem;
    color: var(--v2-text-4);
}

.roles-state p {
    margin: 0;
    font-size: var(--v2-text-sm-size);
}

.roles-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.roles-row {
    display: flex;
    align-items: center;
    gap: var(--v2-space-4);
    padding: 16px 20px;
    border-bottom: 1px solid var(--v2-border-subtle, var(--v2-border));
}

.roles-row:last-child {
    border-bottom: none;
}

.roles-row-avatar {
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--v2-radius-md);
    background: #ecfeff;
    color: var(--v2-primary);
    font-size: 1.05rem;
}

.roles-row-body {
    flex: 1;
    min-width: 0;
}

.roles-row-heading {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.roles-row-title {
    margin: 0;
    font-size: 0.98rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.roles-tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: var(--v2-font-weight-semibold);
}

.roles-tag--system {
    background: #f1f5f9;
    color: #475569;
}

.roles-row-sub {
    margin: 4px 0 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.45;
}

.roles-row-meta {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 8px;
    flex-wrap: wrap;
}

.roles-meta-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.78rem;
    color: var(--v2-text-3);
}

.roles-meta-item--slug {
    font-family: var(--v2-font-mono, monospace);
    color: var(--v2-text-4);
}

.roles-row-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

/* ── Modales ── */
.roles-modal-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.roles-modal-body {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-4);
    min-width: 0;
}

.roles-modal-text {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-2);
    line-height: 1.5;
}

.roles-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.roles-field-label {
    font-size: 0.8rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
}

/**
 * `box-sizing: border-box` obligatoire : sans lui, `width: 100%` s'ajoute aux
 * 24px de padding et aux 2px de bordure, le champ dépasse la largeur de la
 * modale et fait apparaître une barre de défilement horizontale.
 */
.roles-input {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    padding: 9px 12px;
    font-size: var(--v2-text-sm-size);
    font-family: inherit;
    color: var(--v2-text-1);
    background: var(--v2-surface-0);
    outline: none;
    transition: border-color var(--v2-transition-fast);
}

.roles-input:focus {
    border-color: var(--v2-primary);
}

.roles-textarea {
    resize: vertical;
    min-height: 74px;
}

.roles-field-hint {
    margin: 0;
    font-size: 0.75rem;
    color: var(--v2-text-4);
}

.roles-modal-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0;
    padding: 10px 12px;
    border-radius: var(--v2-radius-md);
    background: #ecfeff;
    color: #0e7490;
    font-size: 0.78rem;
    line-height: 1.45;
}

/* ── Responsive ── */
@media (max-width: 900px) {
    .roles-v2 {
        padding: 16px 18px 32px;
    }

    .roles-hero-title {
        font-size: 1.375rem;
    }
}

@media (max-width: 720px) {
    .roles-row {
        flex-wrap: wrap;
        align-items: flex-start;
    }

    .roles-row-actions {
        width: 100%;
        justify-content: flex-end;
        padding-left: 56px;
    }
}

@media (max-width: 480px) {
    .roles-row-actions {
        padding-left: 0;
        flex-wrap: wrap;
    }
}
</style>
